import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { Link } from 'react-router-dom';
import { AuthData } from '../../../context/auth';
import {
	CancellationData,
	withCancellationContext,
} from '../../../context/CancellationProvider';
import { ReinstatementResponse } from '../../../types/cancellations';
import { tableStyles } from '../../../util/styles';
import {
	getComparator,
	HeadCell,
	Order,
	stableSort,
} from '../../../util/tableUtil';
import { capitalize } from '../../../util/util';
import { StyledEmptyMessage } from '../../common';
import SortableTable from '../../SortableTable';

interface Props {
	reinstatements: ReinstatementResponse[];
	authData: AuthData;
	cancellationData: CancellationData;
	openConfirmationModal?: (reinstatement: ReinstatementResponse) => void;
}

interface Data {
	reinstatementId: string;
	dateCreated: string;
	state: string;
	link: string;
}

function createData(
	reinstatementId: string,
	dateCreated: string,
	state: string,
	link: string,
): Data {
	return { reinstatementId, dateCreated, state, link };
}

const ReinstatementsList = (props: Props) => {
	const { reinstatements, authData } = props;
	const currentReinstatements = reinstatements as any;
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('reinstatementId');

	const emptyTable = !currentReinstatements.length ? (
		<StyledEmptyMessage>No Reinstatement</StyledEmptyMessage>
	) : null;
	const classes = tableStyles();

	const headCells: HeadCell[] = [
		{ id: 'reinstatementId', rightAlign: false, label: 'Reinstatement Id' },
		{ id: 'dateCreated', rightAlign: false, label: 'Date Created' },
		{ id: 'state', rightAlign: false, label: 'State' },
	];

	const rows: any = currentReinstatements.map(
		(reinstatement: ReinstatementResponse, index: number) => {
			const displayId = reinstatement.locator ? reinstatement.locator : 'N/A';
			const createdTimestamp = reinstatement.createdTimestamp
				? reinstatement.createdTimestamp
				: Date.now();
			const state = reinstatement.state;
			return createData(
				displayId,
				createdTimestamp.toString(),
				state,
				`${displayId}/details`,
			);
		},
	);

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			return (
				<TableRow
					hover
					tabIndex={-1}
					key={row.reinstatementId}
					component={Link}
					to={row.link}
					className={classes.tableRow}
				>
					<TableCell scope='row'>{row.reinstatementId}</TableCell>
					<TableCell>{authData.getFormattedDate(row.dateCreated)}</TableCell>
					<TableCell>{capitalize(row.state)}</TableCell>
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withCancellationContext<Props>(ReinstatementsList);
